import { AdminPageLayout } from '@/components/admin/AdminPageLayout';
import {
  AgentFacilityFilters,
  AgentFacilityTable,
  BulkActionDialogs,
} from '@/components/admin/agent-facilities';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { trpc } from '@/utils/trpc';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Bot, Building2, Link, Plus, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_protected/admin/agent-facilities')({
  component: AgentFacilitiesPage,
});

function AgentFacilitiesPage() {
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const { organization } = useOrganization();
  const router = useRouter();

  const currentOrganizationId = organization?.id;

  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRelationships, setSelectedRelationships] = useState<Set<string>>(new Set());
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [selectedFacility, setSelectedFacility] = useState<string>('');
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [bulkSelectedAgents, setBulkSelectedAgents] = useState<string[]>([]);
  const [bulkSelectedFacilities, setBulkSelectedFacilities] = useState<string[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    agentId: string;
    facilityId: string;
    agentPhone: string | null;
    facilityName: string | null;
  }>({ isOpen: false, agentId: '', facilityId: '', agentPhone: null, facilityName: null });
  const [bulkConfirmDialog, setBulkConfirmDialog] = useState<{
    isOpen: boolean;
    action: 'assign' | 'remove';
    agentCount: number;
    facilityCount: number;
  }>({ isOpen: false, action: 'assign', agentCount: 0, facilityCount: 0 });

  // tRPC queries
  const agentsWithFacilitiesQuery = trpc.admin.getAgentsWithFacilities.useQuery(
    { organizationId: currentOrganizationId || '' },
    { enabled: !!currentOrganizationId }
  );

  const facilitiesQuery = trpc.admin.getAgentFacilitiesFacilities.useQuery(
    { organizationId: currentOrganizationId || '' },
    { enabled: !!currentOrganizationId }
  );

  const relationshipsQuery = trpc.admin.getAgentFacilities.useQuery(
    {
      organizationId: currentOrganizationId || '',
      limit: 100,
      offset: 0,
      searchQuery: searchTerm,
    },
    { enabled: !!currentOrganizationId }
  );

  // Mutations
  const createRelationshipMutation = trpc.admin.createAgentFacility.useMutation({
    onSuccess: () => {
      toast.success('Relationship created successfully');
      setShowCreateDialog(false);
      setSelectedAgent('');
      setSelectedFacility('');
      relationshipsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to create relationship: ${error.message}`);
    },
  });

  const deleteRelationshipMutation = trpc.admin.deleteAgentFacility.useMutation({
    onSuccess: () => {
      toast.success('Relationship deleted successfully');
      relationshipsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to delete relationship: ${error.message}`);
    },
  });

  const bulkAssignMutation = trpc.admin.bulkAssignFacilities.useMutation({
    onSuccess: () => {
      toast.success('Facilities assigned successfully');
      setShowBulkAssignDialog(false);
      setBulkSelectedAgents([]);
      setBulkSelectedFacilities([]);
      relationshipsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to assign facilities: ${error.message}`);
    },
  });

  const bulkRemoveMutation = trpc.admin.bulkRemoveFacilities.useMutation({
    onSuccess: () => {
      toast.success('Relationships removed successfully');
      setSelectedRelationships(new Set());
      relationshipsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to remove relationships: ${error.message}`);
    },
  });

  // Filtered relationships
  const filteredRelationships = useMemo(() => {
    if (!relationshipsQuery.data?.relationships) return [];
    return relationshipsQuery.data.relationships;
  }, [relationshipsQuery.data?.relationships]);

  // Event handlers
  const handleRowSelect = (agentId: string, facilityId: string) => {
    const key = `${agentId}-${facilityId}`;
    const newSelected = new Set(selectedRelationships);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedRelationships(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allKeys = filteredRelationships.map((r) => `${r.agentId}-${r.facilityId}`);
      setSelectedRelationships(new Set(allKeys));
    } else {
      setSelectedRelationships(new Set());
    }
  };

  const handleDeleteSingle = (
    agentId: string,
    facilityId: string,
    agentPhone: string | null,
    facilityName: string | null
  ) => {
    setConfirmDialog({
      isOpen: true,
      agentId,
      facilityId,
      agentPhone,
      facilityName,
    });
  };

  const handleCreateRelationship = () => {
    if (!selectedAgent || !selectedFacility || !currentOrganizationId) return;

    createRelationshipMutation.mutate({
      organizationId: currentOrganizationId,
      agentId: selectedAgent,
      facilityId: selectedFacility,
    });
  };

  const handleBulkAssign = () => {
    setBulkConfirmDialog({
      isOpen: true,
      action: 'assign',
      agentCount: bulkSelectedAgents.length,
      facilityCount: bulkSelectedFacilities.length,
    });
  };

  const handleBulkRemove = () => {
    const selectedItems = Array.from(selectedRelationships);
    const agentIds = [
      ...new Set(
        selectedItems.map((key) => key.split('-')[0]).filter((id): id is string => id !== undefined)
      ),
    ];
    const facilityIds = [
      ...new Set(
        selectedItems.map((key) => key.split('-')[1]).filter((id): id is string => id !== undefined)
      ),
    ];

    setBulkConfirmDialog({
      isOpen: true,
      action: 'remove',
      agentCount: agentIds.length,
      facilityCount: facilityIds.length,
    });
  };

  const confirmSingleDelete = () => {
    if (!currentOrganizationId) return;

    deleteRelationshipMutation.mutate({
      organizationId: currentOrganizationId,
      agentId: confirmDialog.agentId,
      facilityId: confirmDialog.facilityId,
    });
    setConfirmDialog({
      isOpen: false,
      agentId: '',
      facilityId: '',
      agentPhone: null,
      facilityName: null,
    });
  };

  const confirmBulkAction = () => {
    if (!currentOrganizationId) return;

    if (bulkConfirmDialog.action === 'assign') {
      bulkAssignMutation.mutate({
        organizationId: currentOrganizationId,
        agentIds: bulkSelectedAgents,
        facilityIds: bulkSelectedFacilities,
      });
    } else {
      const selectedItems = Array.from(selectedRelationships);
      const agentIds = [
        ...new Set(
          selectedItems
            .map((key) => key.split('-')[0])
            .filter((id): id is string => id !== undefined)
        ),
      ];
      const facilityIds = [
        ...new Set(
          selectedItems
            .map((key) => key.split('-')[1])
            .filter((id): id is string => id !== undefined)
        ),
      ];

      bulkRemoveMutation.mutate({
        organizationId: currentOrganizationId,
        agentIds,
        facilityIds,
      });
    }
    setBulkConfirmDialog({ isOpen: false, action: 'assign', agentCount: 0, facilityCount: 0 });
  };

  if (isAdminLoading || !isSuperAdmin) {
    return null;
  }

  return (
    <AdminPageLayout
      title="Agent-Facility Management"
      description="Manage which AI agents are assigned to which medical facilities"
      icon={Bot}
      isSuperAdminPage={true}
      headerActions={
        <div className="flex gap-2">
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Relationship
          </Button>
          <Button variant="outline" onClick={() => setShowBulkAssignDialog(true)}>
            <Link className="h-4 w-4 mr-2" />
            Bulk Assign
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Agents</CardTitle>
              <Bot className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {agentsWithFacilitiesQuery.data?.length || 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Facilities</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{facilitiesQuery.data?.length || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Relationships</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{relationshipsQuery.data?.totalCount || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Card>
          <CardHeader>
            <CardTitle>Agent-Facility Relationships</CardTitle>
            <CardDescription>
              View and manage the relationships between agents and facilities
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AgentFacilityFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              selectedCount={selectedRelationships.size}
              onBulkRemove={handleBulkRemove}
              isBulkRemoving={bulkRemoveMutation.isPending}
            />

            <AgentFacilityTable
              relationships={filteredRelationships}
              selectedRelationships={selectedRelationships}
              onRowSelect={handleRowSelect}
              onSelectAll={handleSelectAll}
              onDelete={handleDeleteSingle}
              isLoading={relationshipsQuery.isLoading}
            />
          </CardContent>
        </Card>
      </div>

      <BulkActionDialogs
        showCreateDialog={showCreateDialog}
        setShowCreateDialog={setShowCreateDialog}
        selectedAgent={selectedAgent}
        setSelectedAgent={setSelectedAgent}
        selectedFacility={selectedFacility}
        setSelectedFacility={setSelectedFacility}
        agents={agentsWithFacilitiesQuery.data || []}
        facilities={facilitiesQuery.data || []}
        onCreateRelationship={handleCreateRelationship}
        isCreating={createRelationshipMutation.isPending}
        showBulkAssignDialog={showBulkAssignDialog}
        setShowBulkAssignDialog={setShowBulkAssignDialog}
        bulkSelectedAgents={bulkSelectedAgents}
        setBulkSelectedAgents={setBulkSelectedAgents}
        bulkSelectedFacilities={bulkSelectedFacilities}
        setBulkSelectedFacilities={setBulkSelectedFacilities}
        onBulkAssign={handleBulkAssign}
        isBulkAssigning={bulkAssignMutation.isPending}
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
        onConfirmSingleDelete={confirmSingleDelete}
        bulkConfirmDialog={bulkConfirmDialog}
        setBulkConfirmDialog={setBulkConfirmDialog}
        onConfirmBulkAction={confirmBulkAction}
      />
    </AdminPageLayout>
  );
}
